import React, { useState, useRef, useEffect } from "react";
import { UserIcon, LogoutIcon, ChevronDownIcon } from "../icons";
import ChangePasswordModal from "../modals/ChangePasswordModal";

const ProfileDropdown = ({ user, userRole, isSuperAdmin, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Format user name - construct from firstName/lastName, never show email as name
  const displayName = (() => {
    // Get firstName and lastName, handling null/undefined/empty strings
    const firstName = (user?.firstName && typeof user.firstName === 'string') ? user.firstName.trim() : "";
    const lastName = (user?.lastName && typeof user.lastName === 'string') ? user.lastName.trim() : "";
    
    // Construct full name from firstName and lastName
    if (firstName || lastName) {
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) {
        return fullName;
      }
    }
    
    // Try the pre-formatted name field (if it exists and is not just email)
    if (user?.name && typeof user.name === 'string') {
      const nameValue = user.name.trim();
      // Only use name field if it's not empty and not the same as email
      if (nameValue && nameValue !== user?.email) {
        return nameValue;
      }
    }
    
    // If we have no name at all, show nothing (space reserved for logo later)
    return "";
  })();

  // Format role display
  const displayRole = isSuperAdmin ? "Super Admin" : userRole || "Viewer";

  const avatarInitials = (() => {
    const firstName = (user?.firstName && typeof user.firstName === "string") ? user.firstName.trim() : "";
    const lastName = (user?.lastName && typeof user.lastName === "string") ? user.lastName.trim() : "";
    if (firstName || lastName) {
      const letters = `${firstName.charAt(0)}${lastName.charAt(0)}`.trim();
      if (letters) return letters.toUpperCase();
      return (firstName || lastName).slice(0, 2).toUpperCase();
    }
    if (displayName) {
      const parts = displayName.split(/\s+/).filter(Boolean);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    const email = (user?.email && typeof user.email === "string") ? user.email.trim() : "";
    return email ? email.slice(0, 2).toUpperCase() : "";
  })();

  const handleChangePassword = () => {
    setIsChangePasswordModalOpen(true);
    setIsOpen(false);
  };

  const handleSignOut = () => {
    setIsOpen(false);
    onLogout();
  };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* Profile Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="user-menu-trigger"
          aria-label="Profile menu"
        >
          <span className="user-menu-avatar">
            {avatarInitials || <UserIcon className="w-4 h-4" />}
          </span>
          <span className="user-menu-role">{displayRole}</span>
          <ChevronDownIcon className={`w-3.5 h-3.5 text-white/90 transition-transform hidden lg:block ${isOpen ? "transform rotate-180" : ""}`} />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
            {/* Profile Header */}
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white border border-gray-300 flex items-center justify-center text-gray-600 text-sm font-semibold">
                  <UserIcon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                  {displayName}
                  </p>
                  {/* <p className="text-xs text-gray-500 truncate">
                    {user?.email || ""}
                  </p> */}
                </div>
              </div>
            </div>

            {/* User Information */}
            <div className="px-4 py-3 space-y-2 border-b border-gray-200">
              <div>
                <label className="text-xs font-medium text-gray-500">Organization Name</label>
                <p className="text-sm text-gray-900 mt-0.5">
                  {isSuperAdmin ? displayName : (user?.tenantName || "-")}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Email</label>
                <p className="text-sm text-gray-900 mt-0.5">{user?.email || "-"}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Role</label>
                <p className="text-sm text-gray-900 mt-0.5">{displayRole}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="py-1">
              <button
                onClick={handleChangePassword}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Change Password
              </button>
              <button
                onClick={handleSignOut}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <LogoutIcon className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />
    </>
  );
};

export default ProfileDropdown;
